import { fetchUtils, HttpError } from 'react-admin';

import { makeHttpClient } from '@sh4res/bo-common/utils/build_endpoint';
import { getCurrentScreen } from '@sh4res/bo-common/utils/current_screen';
import { customDataProvider } from '@sh4res/bo-common/utils/custom_data_provider';
import { getDeviceId } from '@sh4res/bo-common/utils/device_id';
import { STORAGE_KEYS } from '@sh4res/bo-common/utils/storage_keys';

import { logApiResponse } from '@Utils/analytics';
import { getApiUrl } from '@Utils/apiOrigin';
import { getAuthSession } from '@Utils/authClient';
import { maintenanceStore } from '@Utils/maintenanceStore';

// Dedupe concurrent session refresh attempts triggered by parallel 401s
let sessionRefresh: Promise<boolean> | null = null;

const refreshSession = async (): Promise<boolean> => {
  sessionRefresh ??= (async () => {
    try {
      const { data } = await getAuthSession(true);
      return Boolean(data);
    } finally {
      sessionRefresh = null;
    }
  })();

  return sessionRefresh;
};

/**
 *
 * Make data provider
 *
 * @param headers Headers to be injected
 * @returns An object containing a react-admin dataProvider and its httpClient
 */
const makeDataProvider = (headers?: [string, string][]) => {
  const apiUrl = getApiUrl();

  const fetchJson = async (url: string, options: fetchUtils.Options = {}) => {
    if (!options.headers) {
      options.headers = new Headers({ Accept: 'application/json' });
    }

    if (headers) {
      // Inject the headers
      for (const header of headers) {
        (options.headers as Headers).set(...header);
      }
    }

    if ('version_sha' in window) {
      (options.headers as Headers).set('bo-version', window.version_sha as string);
    }

    // Tells us which page an endpoint error came from
    (options.headers as Headers).set('bo-current-screen', getCurrentScreen());

    if (localStorage.getItem(STORAGE_KEYS.useLocalDeltaDb) === 'true') {
      (options.headers as Headers).set('x-test-use-local-delta-db', 'true');
    }

    // We want to inject the api url at the httpClient level
    // since the client is used in some parts of the app
    const startedAt = Date.now();
    const method = options.method ?? 'GET';

    // Only the request line and status are ever logged, never bodies: they carry employee
    // and payment data. Successful reads are skipped, they would drown out the trail.
    const logOutcome = (responseStatusCode: number, succeeded: boolean) => {
      if (succeeded && method === 'GET') {
        return;
      }

      logApiResponse({
        url,
        method,
        responseStatusCode,
        durationMs: Date.now() - startedAt,
        succeeded,
      });
    };

    try {
      const response = await fetchUtils.fetchJson(apiUrl + url, options);

      logOutcome(response.status, true);

      return response;
    } catch (error) {
      logOutcome(error instanceof HttpError ? error.status : 0, false);

      if (error instanceof HttpError && error.status === 555) {
        maintenanceStore.activate();
      }

      if (error instanceof HttpError && error.status === 401 && (await refreshSession())) {
        return fetchUtils.fetchJson(apiUrl + url, options);
      }

      throw error;
    }
  };

  const httpClient = makeHttpClient(fetchJson);

  const dataProvider = customDataProvider('', fetchJson);

  return { dataProvider, httpClient };
};

export const { dataProvider, httpClient } = makeDataProvider([['deviceid', getDeviceId()]]);
